import apiBaseUrl from '../../utils/config.js';

class OfficeService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/office`;
  }

  // ✅ Get JWT from localStorage
  getToken() {
    return localStorage.getItem("jwt");
  }

  // ✅ Build Authorization headers
  getAuthHeaders() {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // ✅ Get all offices
  async getAllOffices() {
    const response = await fetch(`${this.baseUrl}/all`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch offices: ${errorText}`);
    }

    return response.json();
  }

  // ✅ Get office by ID
  async getOfficeById(id) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Office not found: ${errorText}`);
    }

    return response.json();
  }

  // ✅ Add office
  async addOffice(office) {
    const response = await fetch(`${this.baseUrl}/add`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(office),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Add office failed: ${errorText}`);
    }

    return response.json();
  }

  // ✅ Update office
  async updateOffice(id, office) {
    const response = await fetch(`${this.baseUrl}/update/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(office),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Update failed: ${errorText}`);
    }

    return response.json();
  }

  // ✅ Update office images
  async updateOfficeImages(id, images) {
    const response = await fetch(`${this.baseUrl}/${id}/update/images`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(images),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Image update failed: ${errorText}`);
    }

    return response.json();
  }

  // ✅ Get office images
  async getOfficeImages(id) {
    const response = await fetch(`${this.baseUrl}/${id}/images`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Images not found: ${errorText}`);
    }

    return response.json();
  }

  // ✅ Delete office
  async deleteOffice(id) {
    const response = await fetch(`${this.baseUrl}/delete/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Delete failed: ${errorText}`);
    }

    return response.text(); // Optional: return status message
  }

  // ✅ Get office name by ID
  async getOfficeNameById(id) {
    const response = await fetch(`${this.baseUrl}/${id}/name`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Office name fetch failed: ${errorText}`);
    }

    return await response.text(); // Returns plain string
  }
}

export default OfficeService;
